import { Fragment } from 'react'
import { Link } from 'react-router'
import { useTranslation } from '#app/utils/i18n.ts'
import { adminNavGroups } from './admin-nav.ts'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuPortal,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuGroup,
} from './ui/dropdown-menu'

/**
 * The below-`lg` half of the admin nav: the same grouped destinations as
 * `AdminSidebar`, behind a «Меню» button. Deliberately the dropdown-menu the
 * codebase already uses for `UserDropdown` — a drawer/Sheet would mean a new
 * dependency for one surface, and Radix already gives keyboard + Escape here.
 */
export function AdminMenu({ className }: { className?: string }) {
	const t = useTranslation()
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="secondary" className={className}>
					{t('nav.menu')}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent
					sideOffset={8}
					align="end"
					// eleven items + four headings can outgrow a short phone viewport.
					// Both axes are spelled out on purpose: the base
					// `DropdownMenuContent` carries `overflow-hidden`, and twMerge does
					// NOT treat `overflow-*` and `overflow-y-*` as one conflict group —
					// a bare `overflow-y-auto` would land beside the base class, not
					// replace it.
					className="max-h-[80vh] overflow-x-hidden overflow-y-auto"
				>
					{adminNavGroups.map((group, index) => {
						const labelId = `admin-menu-${group.labelKey.replace(/\./g, '-')}`
						return (
							<Fragment key={group.labelKey}>
								{index > 0 ? <DropdownMenuSeparator /> : null}
								{/* the label sits INSIDE the group it names, and the group
								    points at it — a label rendered as a sibling labels
								    nothing as far as the accessibility tree is concerned */}
								<DropdownMenuGroup aria-labelledby={labelId}>
									{/* same label voice as the sidebar's group labels
									    (`admin-sidebar.tsx`): weight and tracking mirror it, the
									    font-size stays this menu's own. `font-medium` is doing
									    work — `DropdownMenuLabel`'s base is `font-semibold`, so
									    without it the two surfaces read differently */}
									<DropdownMenuLabel
										id={labelId}
										className="text-muted-foreground text-xs font-medium tracking-wider uppercase"
									>
										{t(group.labelKey)}
									</DropdownMenuLabel>
									{group.items.map((item) => (
										<DropdownMenuItem key={item.to} asChild>
											<Link to={item.to}>{t(item.labelKey)}</Link>
										</DropdownMenuItem>
									))}
								</DropdownMenuGroup>
							</Fragment>
						)
					})}
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
